<!-- Level Configuration -->
<mat-card class="config-card" *ngIf="isLevelSelected && level; else fieldOrEmpty">
  <mat-card-header>
    <mat-icon mat-card-avatar>folder_open</mat-icon>
    <mat-card-title>{{ 'FORM_BUILDER.LEVEL_CONFIG' | translate }}</mat-card-title>
    <mat-card-subtitle>{{ 'FORM_BUILDER.LEVEL_CONFIG_HINT' | translate }}</mat-card-subtitle>
    <button mat-icon-button class="close-btn" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </mat-card-header>

  <mat-card-content class="config-content">
    <form [formGroup]="levelConfigForm">
      <!-- Basic Fields -->
      <div class="config-section">
        <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.BASIC' | translate }}</h4>

        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
          <mat-label>{{ 'FORM_BUILDER.LEVEL_NAME' | translate }}</mat-label>
          <input matInput formControlName="name">
          <mat-error *ngIf="levelConfigForm.get('name')?.hasError('required')">
            {{ 'FORM.REQUIRED' | translate: { name: ('FORM_BUILDER.LEVEL_NAME' | translate) } }}
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
          <mat-label>{{ 'FORM_BUILDER.LEVEL_DESCRIPTION' | translate }}</mat-label>
          <textarea matInput formControlName="description" rows="3"></textarea>
        </mat-form-field>
      </div>

      <!-- Title / Subtitle Fields -->
      <div class="config-section">
        <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.DISPLAY_FIELDS' | translate }}</h4>
        <p class="section-hint">{{ 'FORM_BUILDER.CONFIG.TITLE_FIELD_HINT' | translate }}</p>

        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
          <mat-label>{{ 'FORM_BUILDER.CONFIG.TITLE_FIELD' | translate }}</mat-label>
          <mat-select formControlName="titleFieldId">
            <mat-option value="">{{ 'FORM_BUILDER.CONFIG.NO_TITLE_FIELD' | translate }}</mat-option>
            <mat-option *ngFor="let f of getTitleEligibleFields(level)" [value]="f.id">
              <mat-icon class="mr-1">{{ getFieldTypeIcon(f.type) }}</mat-icon>
              {{ f.label || ('FORM_BUILDER.UNNAMED_FIELD' | translate) }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
          <mat-label>{{ 'FORM_BUILDER.CONFIG.SUBTITLE_FIELD' | translate }}</mat-label>
          <mat-select formControlName="subtitleFieldId">
            <mat-option value="">{{ 'FORM_BUILDER.CONFIG.NO_SUBTITLE_FIELD' | translate }}</mat-option>
            <mat-option *ngFor="let f of getTitleEligibleFields(level)" [value]="f.id">
              <mat-icon class="mr-1">{{ getFieldTypeIcon(f.type) }}</mat-icon>
              {{ f.label || ('FORM_BUILDER.UNNAMED_FIELD' | translate) }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Ordner-Namensfeld (DOCU-745) — nur auf Wurzel-Ebene -->
      <div class="config-section" *ngIf="level.id === 'root'">
        <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.FOLDER' | translate }}</h4>
        <p class="section-hint">{{ 'FORM_BUILDER.CONFIG.FOLDER_FIELD_HINT' | translate }}</p>

        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
          <mat-label>{{ 'FORM_BUILDER.CONFIG.FOLDER_FIELD' | translate }}</mat-label>
          <mat-select formControlName="folderFieldId">
            <mat-option value="">{{ 'FORM_BUILDER.CONFIG.NO_FOLDER_FIELD' | translate }}</mat-option>
            <mat-option *ngFor="let f of getFolderEligibleFields(level)" [value]="f.id">
              <mat-icon class="mr-1">{{ getFieldTypeIcon(f.type) }}</mat-icon>
              {{ f.label || ('FORM_BUILDER.UNNAMED_FIELD' | translate) }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- DOCX-Export (DOCU-664 / DOCU-625) — nur für Sections, nicht für die Wurzel -->
      <div class="config-section" *ngIf="level.id !== 'root'">
        <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.EXPORT' | translate }}</h4>
        <p class="section-hint">{{ 'FORM_BUILDER.CONFIG.EXPORT_HINT' | translate }}</p>

        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
          <mat-label>{{ 'FORM_BUILDER.CONFIG.EXPORT_FORMAT' | translate }}</mat-label>
          <mat-select formControlName="exportFormat">
            <mat-option value="keyvalue">{{ 'FORM_BUILDER.CONFIG.EXPORT_FORMAT_KEYVALUE' | translate }}</mat-option>
            <mat-option value="table">{{ 'FORM_BUILDER.CONFIG.EXPORT_FORMAT_TABLE' | translate }}</mat-option>
            <mat-option value="list">{{ 'FORM_BUILDER.CONFIG.EXPORT_FORMAT_LIST' | translate }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full"
                        *ngIf="levelConfigForm.get('exportFormat')?.value === 'table'">
          <mat-label>{{ 'FORM_BUILDER.CONFIG.EXPORT_TABLE_MARKER' | translate }}</mat-label>
          <input matInput formControlName="exportTableMarker">
          <mat-hint>{{ 'FORM_BUILDER.CONFIG.EXPORT_TABLE_MARKER_HINT' | translate }}</mat-hint>
        </mat-form-field>

        <!-- DOCU-815: Prüfpunkt mit seinen Fotos auf einer Seite zusammenhalten -->
        <mat-checkbox formControlName="exportKeepTogether" class="mt-2">
          <div class="flex items-center gap-1">
            <mat-icon class="text-sm">vertical_align_center</mat-icon>
            {{ 'FORM_BUILDER.CONFIG.EXPORT_KEEP_TOGETHER' | translate }}
          </div>
        </mat-checkbox>

        <!-- DOCU-818: ganzen Prüfpunkt ausblenden, wenn alle Felder leer -->
        <mat-checkbox formControlName="exportHideWhenEmpty" class="mt-2">
          <div class="flex items-center gap-1">
            <mat-icon class="text-sm">unfold_less</mat-icon>
            {{ 'FORM_BUILDER.CONFIG.EXPORT_LEVEL_HIDE_WHEN_EMPTY' | translate }}
          </div>
        </mat-checkbox>
      </div>
    </form>
  </mat-card-content>
</mat-card>

<!-- Field Configuration or Empty -->
<ng-template #fieldOrEmpty>
  <mat-card class="config-card" *ngIf="field; else noSelection">
    <mat-card-header>
      <mat-icon mat-card-avatar>{{ getFieldTypeIcon(field.type) }}</mat-icon>
      <mat-card-title>{{ 'FORM_BUILDER.FIELD_CONFIG' | translate }}</mat-card-title>
      <mat-card-subtitle>{{ getFieldTypeLabel(field.type) | translate }}</mat-card-subtitle>
      <button mat-icon-button class="close-btn" (click)="close()">
        <mat-icon>close</mat-icon>
      </button>
    </mat-card-header>

    <mat-card-content class="config-content">
      <form [formGroup]="configForm">
        <!-- Common Fields -->
        <div class="config-section">
          <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.BASIC' | translate }}</h4>

          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
            <mat-label>{{ 'FORM_BUILDER.CONFIG.LABEL' | translate }}</mat-label>
            <input matInput formControlName="label">
          </mat-form-field>

          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full" *ngIf="field.type !== 'checkbox' && field.type !== 'photo'">
            <mat-label>{{ 'FORM_BUILDER.CONFIG.HINT' | translate }}</mat-label>
            <input matInput formControlName="hint">
            <mat-hint>{{ 'FORM_BUILDER.CONFIG.HINT_DESC' | translate }}</mat-hint>
          </mat-form-field>

          <mat-checkbox formControlName="required">
            {{ 'FORM_BUILDER.CONFIG.REQUIRED' | translate }}
          </mat-checkbox>

          <!-- Photo attachment option (for non-photo fields) -->
          <!-- DOCU-683: Hidden until the inline-photo-on-text-field feature is actually
               implemented in the app (see DOCU-682). The app currently renders a dead
               camera button for allowPhoto with a no-op onPressed, so offering the option
               here only produces a wirkungsloses Flag. Markup, FormControl and i18n keys
               are intentionally kept (not deleted) so the feature can be re-enabled later. -->
          <mat-checkbox *ngIf="false" formControlName="allowPhoto" class="mt-2">
            <div class="flex items-center gap-1">
              <mat-icon class="text-sm">photo_camera</mat-icon>
              {{ 'FORM_BUILDER.CONFIG.ALLOW_PHOTO' | translate }}
            </div>
          </mat-checkbox>

          <!-- Hide field in app (independent of field type) -->
          <mat-checkbox formControlName="hidden" class="mt-2">
            <div class="flex items-center gap-1">
              <mat-icon class="text-sm">visibility_off</mat-icon>
              {{ 'FORM_BUILDER.CONFIG.HIDDEN' | translate }}
            </div>
          </mat-checkbox>

          <!-- DOCU-669: Feld in der App nur anzeigen, nicht bearbeiten.
               Nicht bei der Trennlinie — die hat keinen Wert, den man sperren könnte
               (die App überspringt dekorative Felder beim Readonly-Rendering). -->
          <mat-checkbox *ngIf="field.type !== 'divider'" formControlName="readonly" class="mt-2">
            <div class="flex items-center gap-1">
              <mat-icon class="text-sm">edit_off</mat-icon>
              {{ 'FORM_BUILDER.CONFIG.READONLY' | translate }}
            </div>
          </mat-checkbox>
          <p class="section-hint" *ngIf="field.type !== 'divider'">
            {{ 'FORM_BUILDER.CONFIG.READONLY_HINT' | translate }}
          </p>
          <p class="config-warning" *ngIf="isReadonlyRequiredConflict">
            <mat-icon class="text-sm">warning</mat-icon>
            {{ 'FORM_BUILDER.CONFIG.READONLY_REQUIRED_CONFLICT' | translate }}
          </p>
          <!-- DOCU-931: "Leeres Feld im Export ausblenden" (DOCU-818) ist von hier in den
               Export-Bereich am Ende gezogen — es ist eine Export-Einstellung, keine
               Grundeinstellung. -->
        </div>

        <!-- Initial value -->
        <div class="config-section" *ngIf="field.type === 'text' || field.type === 'textarea' || field.type === 'date' || field.type === 'time' || field.type === 'number' || field.type === 'select'">
          <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.INITIAL_VALUE' | translate }}</h4>

          <!-- Date initial value -->
          <ng-container *ngIf="field.type === 'date'">
            <mat-radio-group formControlName="initialMode" class="flex flex-col gap-1" (change)="onFormChange()">
              <mat-radio-button value="none">{{ 'FORM_BUILDER.CONFIG.INITIAL_NONE' | translate }}</mat-radio-button>
              <mat-radio-button value="now">{{ 'FORM_BUILDER.CONFIG.INITIAL_NOW_DATE' | translate }}</mat-radio-button>
              <mat-radio-button value="fixed">{{ 'FORM_BUILDER.CONFIG.INITIAL_FIXED_DATE' | translate }}</mat-radio-button>
            </mat-radio-group>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full mt-2" *ngIf="configForm.get('initialMode')?.value === 'fixed'">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.INITIAL_FIXED_DATE' | translate }}</mat-label>
              <input matInput formControlName="initial" [placeholder]="'FORM_BUILDER.CONFIG.INITIAL_DATE_PLACEHOLDER' | translate">
            </mat-form-field>
          </ng-container>

          <!-- Time initial value -->
          <ng-container *ngIf="field.type === 'time'">
            <mat-radio-group formControlName="initialMode" class="flex flex-col gap-1" (change)="onFormChange()">
              <mat-radio-button value="none">{{ 'FORM_BUILDER.CONFIG.INITIAL_NONE' | translate }}</mat-radio-button>
              <mat-radio-button value="now">{{ 'FORM_BUILDER.CONFIG.INITIAL_NOW_TIME' | translate }}</mat-radio-button>
              <mat-radio-button value="fixed">{{ 'FORM_BUILDER.CONFIG.INITIAL_FIXED_TIME' | translate }}</mat-radio-button>
            </mat-radio-group>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full mt-2" *ngIf="configForm.get('initialMode')?.value === 'fixed'">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.INITIAL_FIXED_TIME' | translate }}</mat-label>
              <input matInput formControlName="initial" [placeholder]="'FORM_BUILDER.CONFIG.INITIAL_TIME_PLACEHOLDER' | translate">
            </mat-form-field>
          </ng-container>

          <!-- Text / Textarea initial value -->
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full" *ngIf="field.type === 'text' || field.type === 'textarea'">
            <mat-label>{{ 'FORM_BUILDER.CONFIG.INITIAL_VALUE' | translate }}</mat-label>
            <input matInput formControlName="initial">
          </mat-form-field>

          <!-- Number initial value -->
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full" *ngIf="field.type === 'number'">
            <mat-label>{{ 'FORM_BUILDER.CONFIG.INITIAL_VALUE' | translate }}</mat-label>
            <input matInput type="number" formControlName="initial">
          </mat-form-field>

          <!-- Select initial value -->
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full" *ngIf="field.type === 'select'">
            <mat-label>{{ 'FORM_BUILDER.CONFIG.INITIAL_VALUE' | translate }}</mat-label>
            <mat-select formControlName="initial">
              <mat-option [value]="null">{{ 'FORM_BUILDER.CONFIG.INITIAL_NONE' | translate }}</mat-option>
              <mat-option *ngFor="let opt of optionsArray.value" [value]="opt">{{ opt }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Text/Textarea specific -->
        <div class="config-section" *ngIf="field.type === 'text' || field.type === 'textarea'">
          <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.TEXT_OPTIONS' | translate }}</h4>

          <div class="grid grid-cols-2 gap-2">
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.MIN_LENGTH' | translate }}</mat-label>
              <input matInput type="number" formControlName="minLength">
            </mat-form-field>

            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.MAX_LENGTH' | translate }}</mat-label>
              <input matInput type="number" formControlName="maxLength">
            </mat-form-field>
          </div>
        </div>

        <!-- Number specific -->
        <div class="config-section" *ngIf="field.type === 'number'">
          <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.NUMBER_OPTIONS' | translate }}</h4>

          <div class="grid grid-cols-2 gap-2">
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.MIN_VALUE' | translate }}</mat-label>
              <input matInput type="number" formControlName="min">
            </mat-form-field>

            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.MAX_VALUE' | translate }}</mat-label>
              <input matInput type="number" formControlName="max">
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
            <mat-label>{{ 'FORM_BUILDER.CONFIG.DECIMAL_PLACES' | translate }}</mat-label>
            <mat-select formControlName="decimalPlaces">
              <mat-option [value]="0">0 ({{ 'FORM_BUILDER.CONFIG.DECIMAL_PLACES_INTEGER' | translate }})</mat-option>
              <mat-option [value]="1">1</mat-option>
              <mat-option [value]="2">2</mat-option>
              <mat-option [value]="3">3</mat-option>
              <mat-option [value]="4">4</mat-option>
            </mat-select>
            <mat-hint>{{ 'FORM_BUILDER.CONFIG.DECIMAL_PLACES_HINT' | translate }}</mat-hint>
          </mat-form-field>

          <mat-checkbox formControlName="useThousandsSeparator" class="mt-2">
            {{ 'FORM_BUILDER.CONFIG.THOUSANDS_SEPARATOR' | translate }}
          </mat-checkbox>

          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full mt-2">
            <mat-label>{{ 'FORM_BUILDER.CONFIG.UNIT' | translate }}</mat-label>
            <input matInput formControlName="unit" [placeholder]="'FORM_BUILDER.CONFIG.UNIT_PLACEHOLDER' | translate">
            <mat-hint>{{ 'FORM_BUILDER.CONFIG.UNIT_HINT' | translate }}</mat-hint>
          </mat-form-field>
        </div>

        <!-- Photo specific -->
        <div class="config-section" *ngIf="field.type === 'photo'">
          <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.PHOTO_OPTIONS' | translate }}</h4>

          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
            <mat-label>{{ 'FORM_BUILDER.CONFIG.MAX_FILES' | translate }}</mat-label>
            <input matInput type="number" formControlName="maxFiles" min="1" max="20">
            <mat-hint>{{ 'FORM_BUILDER.CONFIG.MAX_FILES_HINT' | translate }}</mat-hint>
          </mat-form-field>

          <mat-checkbox formControlName="showTimestamp" (change)="onFormChange()" class="mt-2">
            <div class="flex items-center gap-1">
              <mat-icon class="text-sm">schedule</mat-icon>
              {{ 'FORM_BUILDER.CONFIG.SHOW_TIMESTAMP' | translate }}
            </div>
          </mat-checkbox>

          <mat-checkbox formControlName="showGeoStamp" (change)="onFormChange()" class="mt-2">
            <div class="flex items-center gap-1">
              <mat-icon class="text-sm">location_on</mat-icon>
              {{ 'FORM_BUILDER.CONFIG.SHOW_GEO_STAMP' | translate }}
            </div>
          </mat-checkbox>

          <mat-checkbox formControlName="writeGeoExif" (change)="onFormChange()" class="mt-2">
            <div class="flex items-center gap-1">
              <mat-icon class="text-sm">location_searching</mat-icon>
              {{ 'FORM_BUILDER.CONFIG.WRITE_GEO_EXIF' | translate }}
            </div>
          </mat-checkbox>

          <!-- DOCU-906: Der Hinweis gehört zu den Stempel-/EXIF-Optionen darüber, nicht zur
               Markierung darunter — sein Schlüssel heißt STAMP_OVERRIDE_HINT. Für die
               Foto-Markierung gibt es gar keine Organisationseinstellung. Stand er unterhalb
               der Markieren-Checkbox, las er sich, als gehöre er zu ihr; seit DOCU-893 ist die
               Checkbox voreingestellt und stand damit direkt über einer falschen Aussage. -->
          <p class="text-xs text-gray-400 mt-2">{{ 'FORM_BUILDER.CONFIG.STAMP_OVERRIDE_HINT' | translate }}</p>

          <mat-checkbox formControlName="allowAnnotation" (change)="onFormChange()" class="mt-2">
            <div class="flex items-center gap-1">
              <mat-icon class="text-sm">edit_location_alt</mat-icon>
              {{ 'FORM_BUILDER.CONFIG.ALLOW_ANNOTATION' | translate }}
            </div>
          </mat-checkbox>
          <!-- DOCU-931: Die beiden Kompakt-Export-Felder (DOCU-815) sind von hier in den
               Export-Bereich am Ende gezogen. -->
        </div>

        <!-- Select options -->
        <div class="config-section" *ngIf="field.type === 'select'">
          <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.SELECT_OPTIONS' | translate }}</h4>

          <div class="options-list" formArrayName="options">
            <div *ngFor="let option of optionsArray.controls; let i = index" class="option-item">
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="flex-1">
                <mat-label>{{ 'FORM_BUILDER.CONFIG.OPTION' | translate }} {{ i + 1 }}</mat-label>
                <input matInput
                  [formControlName]="i"
                  (input)="onOptionChange()"
                  type="text">
              </mat-form-field>
              <button mat-icon-button color="warn" (click)="removeOption(i)" [disabled]="optionsArray.length <= 1">
                <mat-icon>remove_circle</mat-icon>
              </button>
            </div>
          </div>

          <button mat-stroked-button (click)="addOption()" class="w-full mt-2">
            <mat-icon class="mr-1">add</mat-icon>
            {{ 'FORM_BUILDER.CONFIG.ADD_OPTION' | translate }}
          </button>
        </div>

        <!-- Checkbox specific (DOCU-704): konfigurierbares Export-Wertepärchen -->
        <div class="config-section" *ngIf="field.type === 'checkbox'">
          <h4 class="section-title">{{ 'FORM_BUILDER.CONFIG.CHECKBOX_OPTIONS' | translate }}</h4>

          <div formGroupName="checkboxOptions">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.CHECKBOX_TRUE_LABEL' | translate }}</mat-label>
              <input matInput formControlName="trueLabel">
            </mat-form-field>

            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.CHECKBOX_FALSE_LABEL' | translate }}</mat-label>
              <input matInput formControlName="falseLabel">
            </mat-form-field>
          </div>

          <button mat-stroked-button type="button" (click)="applyCheckboxGlyphPreset()" class="w-full">
            <mat-icon class="mr-1">check_box</mat-icon>
            {{ 'FORM_BUILDER.CONFIG.CHECKBOX_GLYPH_PRESET' | translate }}
          </button>

          <!-- DOCU-746: SVG-Visual (z. B. Toggle-Button); hat im Export Vorrang vor dem Text -->
          <h4 class="section-title mt-4">{{ 'FORM_BUILDER.CONFIG.CHECKBOX_SVG_TITLE' | translate }}</h4>

          <div formGroupName="checkboxOptions">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.CHECKBOX_TRUE_SVG' | translate }}</mat-label>
              <textarea matInput rows="2" formControlName="trueSvg" spellcheck="false"></textarea>
            </mat-form-field>
            <div class="svg-preview" *ngIf="trueSvgPreview" [innerHTML]="trueSvgPreview"></div>

            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
              <mat-label>{{ 'FORM_BUILDER.CONFIG.CHECKBOX_FALSE_SVG' | translate }}</mat-label>
              <textarea matInput rows="2" formControlName="falseSvg" spellcheck="false"></textarea>
            </mat-form-field>
            <div class="svg-preview" *ngIf="falseSvgPreview" [innerHTML]="falseSvgPreview"></div>
          </div>

          <div class="flex gap-2">
            <button mat-stroked-button type="button" (click)="applyToggleSvgPreset()" class="flex-1">
              <mat-icon class="mr-1">toggle_on</mat-icon>
              {{ 'FORM_BUILDER.CONFIG.CHECKBOX_TOGGLE_PRESET' | translate }}
            </button>
            <button mat-stroked-button type="button" (click)="clearToggleSvgPreset()"
                    [attr.aria-label]="'FORM_BUILDER.CONFIG.CHECKBOX_SVG_CLEAR' | translate"
                    [attr.title]="'FORM_BUILDER.CONFIG.CHECKBOX_SVG_CLEAR' | translate">
              <mat-icon>clear</mat-icon>
            </button>
          </div>

          <p class="text-xs text-gray-400 mt-2">{{ 'FORM_BUILDER.CONFIG.CHECKBOX_OPTIONS_HINT' | translate }}</p>
        </div>

        <!-- DOCU-931: Export-Optionen gebündelt und eingeklappt.
             Sie lagen bisher verstreut — "Leeres Feld ausblenden" bei den
             Grundeinstellungen, die beiden Kompakt-Export-Felder unten bei den
             Fotooptionen. Alle drei sind Opt-in mit Default "aus" (DOCU-815/818),
             tragen aber die längsten Hinweistexte des Panels.
             Einklappen heißt hier nicht verstecken: Der Bereich öffnet sich von
             selbst, sobald an diesem Feld etwas gesetzt ist, und trägt im
             geschlossenen Zustand eine Markierung. -->
        <div class="config-section export-section">
          <button type="button" class="section-toggle" (click)="toggleExportSection()"
                  [attr.aria-expanded]="exportSectionOpen">
            <mat-icon class="toggle-chevron" [class.is-open]="exportSectionOpen">chevron_right</mat-icon>
            <span class="section-title">{{ 'FORM_BUILDER.CONFIG.EXPORT' | translate }}</span>
            <span class="section-badge" *ngIf="hasExportSettings && !exportSectionOpen">
              {{ 'FORM_BUILDER.CONFIG.EXPORT_SET' | translate }}
            </span>
          </button>

          <div class="section-body" *ngIf="exportSectionOpen">
            <!-- DOCU-818: leeres Feld im Export ausblenden -->
            <mat-checkbox formControlName="exportHideWhenEmpty" (change)="onFormChange()">
              <div class="flex items-center gap-1">
                <mat-icon class="text-sm">unfold_less</mat-icon>
                {{ 'FORM_BUILDER.CONFIG.EXPORT_HIDE_WHEN_EMPTY' | translate }}
              </div>
            </mat-checkbox>

            <!-- DOCU-815: Kompakt-Export der Fotos (leer = Renderer-Default) -->
            <ng-container *ngIf="field.type === 'photo'">
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full mt-3">
                <mat-label>{{ 'FORM_BUILDER.CONFIG.EXPORT_MAX_IMAGE_CM' | translate }}</mat-label>
                <input matInput type="number" formControlName="exportMaxImageCm" min="1" max="20" step="0.5">
                <mat-hint>{{ 'FORM_BUILDER.CONFIG.EXPORT_MAX_IMAGE_CM_HINT' | translate }}</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
                <mat-label>{{ 'FORM_BUILDER.CONFIG.EXPORT_COLUMNS' | translate }}</mat-label>
                <input matInput type="number" formControlName="exportColumns" min="1" max="4">
                <mat-hint>{{ 'FORM_BUILDER.CONFIG.EXPORT_COLUMNS_HINT' | translate }}</mat-hint>
              </mat-form-field>
            </ng-container>
          </div>
        </div>
      </form>
    </mat-card-content>
  </mat-card>

  <!-- No element selected -->
  <ng-template #noSelection>
    <mat-card class="config-card empty-card">
      <div class="empty-state">
        <mat-icon class="empty-icon">touch_app</mat-icon>
        <h4>{{ 'FORM_BUILDER.SELECT_ELEMENT' | translate }}</h4>
        <p>{{ 'FORM_BUILDER.SELECT_ELEMENT_HINT' | translate }}</p>
      </div>
    </mat-card>
  </ng-template>
</ng-template>